import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

export type NotificationsState =
  | "unsupported"
  | "denied"
  | "default"
  | "granted-unsubscribed"
  | "subscribed";

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from(Array.from(raw, (c) => c.charCodeAt(0)));
}

function supported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export function useNotifications() {
  const [state, setState] = useState<NotificationsState>("default");

  const refresh = useCallback(async () => {
    if (!supported()) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        setState("subscribed");
      } else if (Notification.permission === "granted") {
        setState("granted-unsubscribed");
      } else {
        setState("default");
      }
    } catch {
      // No SW registered yet (dev without preview): treat as default.
      setState("default");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!supported()) return;
    if (Notification.permission !== "granted") {
      const p = await Notification.requestPermission();
      if (p !== "granted") {
        setState(p === "denied" ? "denied" : "default");
        return;
      }
    }
    const { publicKey } = await api.get<{ publicKey: string | null }>(
      "/notifications/public-key",
    );
    if (!publicKey) {
      console.warn("[useNotifications] server returned no VAPID public key — set VAPID_PUBLIC_KEY");
      return;
    }
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      // Safeguard: if a stale sub exists (old VAPID key, say), drop it before resubscribing
      await existing.unsubscribe().catch(() => {});
    }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast: TS 5.7's Uint8Array is generic over ArrayBufferLike, but
      // PushManager expects a BufferSource (ArrayBuffer-backed).
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
    });
    const json = sub.toJSON() as {
      endpoint: string;
      keys: { p256dh: string; auth: string };
    };
    await api.post("/notifications/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      userAgent: navigator.userAgent,
    });
    setState("subscribed");
  }, []);

  const unsubscribe = useCallback(async () => {
    if (!supported()) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      try {
        await api.delete(`/notifications/subscribe?endpoint=${encodeURIComponent(endpoint)}`);
      } catch {
        // Backend might have already removed it — ignore.
      }
    }
    setState(Notification.permission === "granted" ? "granted-unsubscribed" : "default");
  }, []);

  const testPush = useCallback(async () => {
    await api.post("/notifications/test");
  }, []);

  return { state, subscribe, unsubscribe, refresh, testPush };
}
